import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../components/icons.js';
import { StatusPill, TechAvatar, fmtDate, fmtMoney } from '../components/shell.js';
import { clients, jobs, invoices, techs } from '../data/seed.js';

type Tab = 'overview' | 'jobs' | 'invoices' | 'notes';

export function ClientDetailPage() {
  const navigate = useNavigate();
  const { id: clientId } = useParams<{ id: string }>();
  const client = clients.find((c) => c.id === clientId);
  const [tab, setTab] = useState<Tab>('overview');

  if (!client)
    return (
      <div className="page">
        <div className="empty-state">
          Client not found.{' '}
          <button className="btn" onClick={() => navigate('/clients')}>
            Back
          </button>
        </div>
      </div>
    );

  const parent = client.parentId ? clients.find((c) => c.id === client.parentId) : null;
  const subClients = clients.filter((c) => c.parentId === client.id);
  const jobsForClient = jobs
    .filter((j) => j.clientId === client.id)
    .sort(
      (a, b) => new Date(b.scheduledStart).getTime() - new Date(a.scheduledStart).getTime(),
    );
  const invoicesForClient = invoices.filter((i) => i.clientId === client.id);
  const techById = Object.fromEntries(techs.map((t) => [t.id, t]));

  return (
    <div className="page">
      <div style={{ marginBottom: 12 }}>
        <button className="btn ghost sm" onClick={() => navigate('/clients')}>
          {Icon.arrowLeft({ size: 14 })} All clients
        </button>
      </div>

      {/* Header card */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div className="avatar lg" style={{ background: 'hsl(var(--muted))' }}>
            {client.name
              .split(' ')
              .map((w) => w[0])
              .slice(0, 2)
              .join('')
              .toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2
                style={{ margin: 0, fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em' }}
              >
                {client.name}
              </h2>
              <span className="pill outline">{client.kind}</span>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 12,
                marginTop: 4,
                color: 'hsl(var(--muted-foreground))',
                fontSize: 13,
                flexWrap: 'wrap',
              }}
            >
              <span className="mono" style={{ fontSize: 12.5 }}>
                {client.phone}
              </span>
              {client.email && <span>{client.email}</span>}
              <span>Client since {fmtDate(client.createdAt, { year: 'numeric' })}</span>
            </div>
            {parent && (
              <div
                style={{ marginTop: 6, fontSize: 12.5, color: 'hsl(var(--muted-foreground))' }}
              >
                Sub-client of{' '}
                <a
                  onClick={() => navigate('/clients/' + parent.id)}
                  style={{
                    color: 'hsl(var(--primary))',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  {parent.name}
                </a>
              </div>
            )}
            <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {client.tags.map((t) => (
                <span key={t} className="tag">
                  {t}
                </span>
              ))}
              <button
                className="tag"
                style={{
                  border: '1px dashed hsl(var(--border))',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              >
                + tag
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn">{Icon.phone({ size: 14 })} Call</button>
            <button className="btn">{Icon.msg({ size: 14 })} SMS</button>
            <button className="btn">{Icon.mail({ size: 14 })} Email</button>
          </div>
        </div>
      </div>

      <div className="tab-bar">
        <button
          className={'tab ' + (tab === 'overview' ? 'active' : '')}
          onClick={() => setTab('overview')}
        >
          Overview
        </button>
        <button
          className={'tab ' + (tab === 'jobs' ? 'active' : '')}
          onClick={() => setTab('jobs')}
        >
          Jobs ({jobsForClient.length})
        </button>
        <button
          className={'tab ' + (tab === 'invoices' ? 'active' : '')}
          onClick={() => setTab('invoices')}
        >
          Invoices ({invoicesForClient.length})
        </button>
        <button
          className={'tab ' + (tab === 'notes' ? 'active' : '')}
          onClick={() => setTab('notes')}
        >
          Notes
        </button>
      </div>

      {tab === 'overview' && (
        <div className="grid-2">
          <div className="stack gap-4">
            <div className="card">
              <div className="card-title">
                <span>Addresses</span>
                <button className="btn ghost sm">{Icon.plus({ size: 12 })} Add</button>
              </div>
              <div className="stack gap-3">
                {client.addresses.map((a, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10 }}>
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 6,
                        background: 'hsl(var(--muted))',
                        display: 'grid',
                        placeItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {Icon.mapPin({ size: 14 })}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: 'hsl(var(--muted-foreground))',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          fontWeight: 500,
                        }}
                      >
                        {a.label}
                      </div>
                      <div style={{ fontSize: 13.5 }}>
                        {a.line1}
                        {a.line2 ? ', ' + a.line2 : ''}
                      </div>
                      <div style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))' }}>
                        {a.city}, {a.state} {a.zip}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {subClients.length > 0 && (
              <div className="card">
                <div className="card-title">
                  <span>Sub-clients</span>
                  <span className="muted">{subClients.length} rentals</span>
                </div>
                <div className="stack gap-2">
                  {subClients.map((s) => (
                    <div key={s.id} className="row" onClick={() => navigate('/clients/' + s.id)}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 6,
                          background: 'hsl(var(--muted))',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        {Icon.home2({ size: 14 })}
                      </div>
                      <div className="row-main">
                        <div className="row-title">{s.name}</div>
                        <div className="row-sub">
                          {s.addresses[0]!.line1}, {s.addresses[0]!.city}
                        </div>
                      </div>
                      <div className="row-aside">{Icon.chevronRight({ size: 14 })}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-title">
                <span>Recent jobs</span>
                <button className="btn ghost sm" onClick={() => setTab('jobs')}>
                  View all {Icon.chevronRight({ size: 12 })}
                </button>
              </div>
              <div className="row-list">
                {jobsForClient.slice(0, 4).map((j) => (
                  <div className="row" key={j.id} onClick={() => navigate('/jobs/' + j.id)}>
                    <div className="row-time mono" style={{ fontSize: 11 }}>
                      {j.id.replace('JOB-2026-', '#')}
                    </div>
                    <div className="row-main">
                      <div className="row-title">
                        {j.appliance.type} · {j.appliance.brand}
                      </div>
                      <div className="row-sub">
                        {fmtDate(j.scheduledStart)} · {j.problem}
                      </div>
                    </div>
                    <div className="row-aside">
                      <StatusPill status={j.status} />
                    </div>
                  </div>
                ))}
                {jobsForClient.length === 0 && <div className="empty-state">No jobs yet</div>}
              </div>
            </div>
          </div>

          <div className="stack gap-4">
            <div className="card">
              <div className="card-title">
                <span>Internal notes</span>
                <button className="btn ghost sm">{Icon.edit({ size: 12 })} Edit</button>
              </div>
              <div
                style={{
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: 'hsl(var(--foreground))',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {client.notes || <span className="muted">No notes yet.</span>}
              </div>
            </div>
            <div className="card">
              <div className="card-title">
                <span>Stats</span>
              </div>
              <div className="stack gap-3">
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span className="muted">Total jobs</span>
                  <span style={{ fontWeight: 500 }}>{jobsForClient.length}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span className="muted">Completed</span>
                  <span style={{ fontWeight: 500 }}>
                    {jobsForClient.filter((j) => j.status === 'Completed').length}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span className="muted">Lifetime revenue</span>
                  <span style={{ fontWeight: 500 }}>
                    {fmtMoney(
                      invoicesForClient
                        .filter((i) => i.status === 'Paid')
                        .reduce((s, i) => s + i.paid, 0),
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span className="muted">Outstanding</span>
                  <span
                    style={{
                      fontWeight: 500,
                      color: invoicesForClient.some((i) => i.status === 'Overdue')
                        ? 'hsl(var(--destructive))'
                        : 'inherit',
                    }}
                  >
                    {fmtMoney(
                      invoicesForClient
                        .filter((i) => i.status !== 'Paid')
                        .reduce((s, i) => s + (i.total - i.paid), 0),
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'jobs' && (
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Date</th>
                <th>Appliance</th>
                <th>Problem</th>
                <th>Tech</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {jobsForClient.map((j) => (
                <tr key={j.id} onClick={() => navigate('/jobs/' + j.id)}>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {j.id}
                  </td>
                  <td style={{ fontSize: 13 }}>
                    {fmtDate(j.scheduledStart, { year: 'numeric' })}
                  </td>
                  <td>
                    {j.appliance.type} · <span className="muted">{j.appliance.brand}</span>
                  </td>
                  <td
                    style={{
                      maxWidth: 320,
                      fontSize: 13,
                      color: 'hsl(var(--muted-foreground))',
                    }}
                  >
                    {j.problem}
                  </td>
                  <td>{techById[j.techId] && <TechAvatar tech={techById[j.techId]} size="sm" />}</td>
                  <td>
                    <StatusPill status={j.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'invoices' && (
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Job</th>
                <th>Issued</th>
                <th>Due</th>
                <th>Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {invoicesForClient.map((i) => (
                <tr key={i.id}>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {i.id}
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {i.jobId}
                  </td>
                  <td style={{ fontSize: 13 }}>{fmtDate(i.issued)}</td>
                  <td style={{ fontSize: 13 }}>{fmtDate(i.due)}</td>
                  <td style={{ fontWeight: 500 }}>{fmtMoney(i.total)}</td>
                  <td>
                    <span
                      className={
                        'pill ' +
                        (i.status === 'Paid'
                          ? 'completed'
                          : i.status === 'Overdue'
                          ? 'cancel'
                          : 'scheduled')
                      }
                    >
                      <span className="dot" />
                      {i.status}
                    </span>
                  </td>
                </tr>
              ))}
              {invoicesForClient.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-state">
                    No invoices
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'notes' && (
        <div className="card">
          <div className="card-title">
            <span>Internal notes</span>
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {client.notes || <span className="muted">No notes yet.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
